/**
 * 库存水位（P-02）。
 *
 * 用横向水位条 + 数字，不用饼图 —— 规范明确「无图表装饰」。
 * 低于阈值的行前置 `triangle-alert` 并给出直达「生成批次」的入口：
 * 报警而不给出下一步动作，等于把判断成本又推回给运营者。
 */
import { Link } from 'react-router-dom';
import { Icon } from '../../icons/Icon';
import { CardSkeleton, EmptyFirstRun, ErrorBlock } from '../../components/DataState';
import { describeError } from '../../api/client';
import type { AsyncState } from '../../lib/useAsync';
import type { InventoryRow } from '../../api/endpoints';
import { INVENTORY_LOW_WATERMARK } from '../../mocks/data';
import { ICON_SIZE } from '../../tokens';
import { formatCount } from '../../lib/format';

export interface InventoryWatermarksProps {
  readonly state: AsyncState<InventoryRow[]>;
  readonly onRetry: () => void;
}

export function InventoryWatermarks({ state, onRetry }: InventoryWatermarksProps) {
  return (
    <section aria-labelledby="inventory-heading" style={{ marginTop: 'var(--space-6)' }}>
      <h2 className="section-title" id="inventory-heading">
        发卡库存水位
      </h2>

      {state.kind === 'loading' ? <CardSkeleton lines={3} /> : null}

      {state.kind === 'failed' ? (
        <div className="card">
          <ErrorBlock message={describeError(state.error, '加载库存水位')} onRetry={onRetry} />
        </div>
      ) : null}

      {state.kind === 'ready' && state.data.length === 0 ? (
        <div className="card">
          <EmptyFirstRun
            icon="boxes"
            description="还没有生成过授权码批次。批次是发卡库存的来源，生成后可导出 CSV 上架到发卡网站。"
            action={
              <Link to="/batches" className="btn btn--primary">
                <Icon name="plus" size={ICON_SIZE.control} />
                生成第一个批次
              </Link>
            }
          />
        </div>
      ) : null}

      {state.kind === 'ready' && state.data.length > 0 ? (
        <div className="card">
          {state.data.map((row) => {
            const sellable = row.available + row.exported;
            const totalIssued = sellable + row.redeemed;
            const ratio = totalIssued === 0 ? 0 : Math.round((sellable / totalIssued) * 100);
            const low = sellable < INVENTORY_LOW_WATERMARK;
            return (
              <div className="watermark-row" key={row.product_id}>
                <span className="watermark-row__name">
                  <Icon name="package" size={ICON_SIZE.inline} />
                  <span
                    style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                    title={row.product_name}
                  >
                    {row.product_name}
                  </span>
                </span>

                <span
                  className="watermark-row__track"
                  role="progressbar"
                  aria-valuenow={ratio}
                  aria-valuemin={0}
                  aria-valuemax={100}
                  aria-label={`${row.product_name} 可售占比`}
                >
                  <span
                    className="watermark-row__fill"
                    data-low={low ? 'true' : 'false'}
                    style={{ width: `${ratio}%` }}
                  />
                </span>

                <span className="watermark-row__value">
                  可售 {formatCount(sellable)} / 已兑换 {formatCount(row.redeemed)}
                </span>

                {low ? (
                  <span className="watermark-row__warn">
                    <Icon name="triangle-alert" size={ICON_SIZE.inline} />
                    低于 {formatCount(INVENTORY_LOW_WATERMARK)}
                    <Link to="/batches" className="link-button">
                      生成批次
                    </Link>
                  </span>
                ) : null}
              </div>
            );
          })}
        </div>
      ) : null}
    </section>
  );
}
